import { trigger, style, transition, animate, group } from "@angular/animations";
import {
	Component,
	ViewEncapsulation,
	Input,
	Output,
	EventEmitter,
	forwardRef,
	ElementRef,
	OnDestroy,
	ChangeDetectorRef
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { IdService } from "../shared/services/id.service";
import { IGenericSelectOption } from "../shared/types/forms";
@Component({
	selector: "rbc-multiselect",
	templateUrl: "./multiselect.component.html",
	encapsulation: ViewEncapsulation.None,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: forwardRef(() => MultiselectComponent)
		}
	],
	animations: [
		trigger('max-height', [
			transition(
				':leave',
				group([
				  animate('0.4s ease-in-out', style({ height: 0 })),
				  animate('0.4s steps(1,start)', style({ overflow: 'hidden' }))
				])
			),
			transition(
				':enter', [
					style({ height: 0, overflow: 'hidden' }),
					group([
						animate('0.4s ease-in-out', style({ height: '*' })),
						animate('0.4s steps(1,end)', style({ overflow: 'hidden' }))
					])
				]
			)
		])
	],
})
export class MultiselectComponent implements ControlValueAccessor, OnDestroy {
	@Input() id: string;
	@Input() checkedText = "checked";
	@Input() disabled: boolean;
	@Input() displayTotal: boolean;
	@Input() height;
	@Input() helpText: string;
	@Input() label: string;
	@Input() labelSrOnly: string;
	@Input() options: IGenericSelectOption[];
	@Input() placeholder = "Choose";
	@Input() size: "large" | "medium" | "small" = "medium";
	@Input() sizeWidth: string;

	@Input() status: string | boolean;
	@Input() statusType: "danger" | "success" | "warning" | "info" = "info";

	@Output() selectUpdate = new EventEmitter<IGenericSelectOption[]>();
	@Output() dropdownClosed = new EventEmitter<IGenericSelectOption[]>();
	@Output() dropdownOpen = new EventEmitter<any>();

	clickListener = this.clickOutside.bind(this);
	activeTransition = "max-height"
	index = 0;
	open = false;
	highlightID = 0;
	filteredOptions;

	constructor(private idService: IdService, private eRef: ElementRef, private cdr: ChangeDetectorRef) {
		this.id = idService.generate();
	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
		document.removeEventListener("click", this.clickListener);
	}

	// Function to call when the rating changes.
	onChange = (item) => { };
	// Function to call when the input is touched (when a star is clicked).
	onTouched = () => { };

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	writeValue(value: any): void {
		this.filteredOptions = value;
		this.selectUpdate.emit(value);
	}

	$_Multiselect_open(e = null) {
		this.open = !this.open && !(this.disabled && !this.disabled);
		document.addEventListener("click", this.clickListener);

		if(this.open){
			this.dropdownOpen.emit(e)
		}
		// if menu currently open don't add another event handler
		if (!this.open) {
			this.$_SelectDropdown_close();
		}
	}

	// remove the event listener if null prop, or target is not the dropdown and not one of list elements
	$_SelectDropdown_close() {
		this.open = false;
		this.cdr.detectChanges();
		this.dropdownClosed.emit(this.filteredOptions);
		document.removeEventListener("click", this.clickListener);
	}

	$_SelectDropdown_updateHighlight(e) {
		const key = e.key.toLowerCase();
		const options = this.options;
		const nbOptions = options.length;
		if (nbOptions > 0 && this.open) {
			// if key press down
			if (key.indexOf("down") > -1) {
				e.preventDefault();
				this.index = this.$_SelectDropdown_nextEligibleOption(
					this.index,
					this.options,
					nbOptions
				);
				this.highlightID = this.index;
			} else if (key.indexOf("up") > -1) {
				e.preventDefault();
				this.index = this.$_SelectDropdown_previousEligibleOption(
					this.index,
					this.options
				);
				this.highlightID = this.index;
			} else if (key === "enter" && !this.options[this.index].disabled || key === " ") {
				e.preventDefault();
				this.updateSelected(this.options[this.index], this.index);
			} else if (key === "escape") {
				e.preventDefault();
				this.open = false;
				this.$_SelectDropdown_close();
			}
		} else if (
			key.indexOf("down") > -1 ||
			key.indexOf("up") > -1 ||
			key === "enter" ||
			key === " "
		) {
			e.preventDefault();
			this.$_Multiselect_open();
		}
	}

	$_SelectDropdown_nextEligibleOption(index, options, nbOptions) {
		let nextIndex = index + 1;
		while (nextIndex < nbOptions && options[nextIndex].disabled) {
			nextIndex += 1;
		}
		// if next index was not past end return it else return original
		return nextIndex !== nbOptions ? nextIndex : index;
	}

	$_SelectDropdown_previousEligibleOption(index, options) {
		let nextIndex = index - 1;
		while (nextIndex > -1 && options[nextIndex].disabled) {
			nextIndex -= 1;
		}
		// if next index was not past end return it else return original
		return nextIndex > -1 ? nextIndex : index;
	}

	updateSelected(el, fromKeyPress) {
		if (typeof el.disabled === "undefined" || !el.disabled) {

			this.options[fromKeyPress].selected = !this.options[fromKeyPress].selected;
		}

		let items = this.options.filter(o => o.selected);

		this.filteredOptions = items;
		this.onChange(items);
		this.selectUpdate.emit(items);
	}

	selectedOptionsLabel() {
		return this.options
			.filter(el => el.selected)
			.map(el => {
				return el.label;
			})
			.join(", ");
	}

	// Creates the formatted label on the multiselect
	selectedLabelFormatted() {
		if(this.options){
		const nbSelected = this.options.filter(option => option.selected)
			.length;
		const total = this.displayTotal ? " / " + this.options.length : "";
		return nbSelected > 0
			? nbSelected + total + " " + this.checkedText
			: this.placeholder;
		}
	}

	selectedOptionsId() {
		const _self = this;

		if (_self.open && _self.highlightID >= 0) {
			if(_self.options[_self.highlightID]){
				let selectedElement = `${_self.id}Option${_self.options[_self.highlightID].value.replace(/[^a-zA-Z0-9-]/g, "")}`;
				return selectedElement;
			}
		} else {
			return null;
		}
	}

	clickOutside(e) {
		if (e === "escape") {
			this.open = false;
		}
		if (!this.eRef.nativeElement.contains(e.target) && this.open) {
			this.$_SelectDropdown_close();
		}
	}

	idReplace(option) {
		return option.value.replace(/[^a-zA-Z0-9-]/g, "");
	}

	classGen() {
		return {
			[`rbc-multiselect-${this.size}`]: this.size,
			[`rbc-multiselect-width-${this.sizeWidth}`]: this.sizeWidth,
			disabled: this.disabled && !this.disabled
		};
	}

	heightGen() {
		return (this.height && typeof this.height !== "string") ? `${this.height}px` : this.height;

	}

	last() {
		const _self = this;
		let oL = _self.options.length - 1;
		return _self.options[oL];
	}
}
